// RideRelease Enhanced Offline Status Hook
// Integrated with offline forms store and quality detection

import { useState, useEffect, useCallback } from 'react';
import { useOfflineStatus as useStoreOfflineStatus, useOfflineFormsStore } from '@/stores/offlineFormsStore';

interface NetworkConnection {
  effectiveType?: '2g' | '3g' | '4g' | 'slow-2g';
  type?: 'bluetooth' | 'cellular' | 'ethernet' | 'wifi' | 'wimax' | 'none' | 'unknown';
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
}

// Extend navigator type to include connection
declare global {
  interface Navigator {
    connection?: NetworkConnection;
    mozConnection?: NetworkConnection;
    webkitConnection?: NetworkConnection;
  }
}

interface QualityTestResult {
  latency: number;
  bandwidth: number;
  quality: 'good' | 'poor' | 'offline';
}

export function useOfflineStatus() {
  const storeStatus = useStoreOfflineStatus();
  const [isRunningQualityTest, setIsRunningQualityTest] = useState(false);
  
  // Get network connection info
  const getNetworkConnection = (): NetworkConnection | null => {
    return navigator.connection || navigator.mozConnection || navigator.webkitConnection || null;
  };

  // Detect connection type
  const getConnectionType = useCallback((): 'wifi' | 'cellular' | 'unknown' => {
    const connection = getNetworkConnection();
    
    if (!connection) return 'unknown';
    
    if (connection.type === 'wifi') {
      return 'wifi';
    } else if (connection.type === 'cellular') {
      return 'cellular';
    } else if (connection.effectiveType && ['4g', '3g'].includes(connection.effectiveType)) {
      return connection.effectiveType === '4g' ? 'wifi' : 'cellular';
    }
    
    return 'unknown';
  }, []);

  // Test network quality with actual HTTP requests
  const testNetworkQuality = useCallback(async (): Promise<QualityTestResult> => {
    if (!navigator.onLine) {
      return { latency: Infinity, bandwidth: 0, quality: 'offline' };
    }

    try {
      const startTime = performance.now();
      
      // Test with a small image to measure latency and basic connectivity
      const testUrl = `${window.location.origin}/favicon.ico?t=${Date.now()}`;
      const response = await fetch(testUrl, {
        method: 'HEAD',
        cache: 'no-cache',
        mode: 'no-cors' // Avoid CORS issues
      });
      
      const endTime = performance.now();
      const latency = endTime - startTime;
      
      // Estimate quality based on latency and connection info
      const connection = getNetworkConnection();
      let quality: 'good' | 'poor' | 'offline' = 'good';
      
      if (latency > 2000) {
        quality = 'poor';
      } else if (latency > 1000) {
        quality = connection?.effectiveType === '2g' || connection?.effectiveType === 'slow-2g' ? 'poor' : 'good';
      }
      
      return {
        latency,
        bandwidth: connection?.downlink || 0,
        quality
      };
    } catch (error) {
      console.warn('[NetworkQuality] Test failed:', error);
      return { latency: Infinity, bandwidth: 0, quality: 'poor' };
    }
  }, [getNetworkConnection]);

  // Update store with connection status - avoid circular dependency
  const updateConnectionStatus = useCallback((overrides: Partial<any> = {}) => {
    const isOnline = navigator.onLine;
    const now = new Date();
    
    // Get current state and update function directly from store to avoid circular dependency
    const currentState = useOfflineFormsStore.getState();
    const currentConnection = currentState.connection;
    
    currentState.updateConnectionStatus({
      isOnline,
      lastOnlineAt: isOnline ? now : currentConnection.lastOnlineAt,
      offlineDuration: !isOnline && currentConnection.lastOnlineAt 
        ? now.getTime() - currentConnection.lastOnlineAt.getTime()
        : 0,
      networkType: getConnectionType(),
      ...overrides
    });
  }, [getConnectionType]); // Remove storeStatus dependency to break circular updates

  // Run periodic quality tests
  const runQualityTest = useCallback(async () => {
    if (isRunningQualityTest || !navigator.onLine) return;
    
    setIsRunningQualityTest(true);
    
    try {
      const qualityResult = await testNetworkQuality();
      // Note: Removed updateConnectionStatus call to avoid React errors
      
      console.log('[NetworkQuality]', {
        quality: qualityResult.quality,
        latency: `${qualityResult.latency.toFixed(0)}ms`,
        bandwidth: qualityResult.bandwidth ? `${qualityResult.bandwidth}Mbps` : 'unknown'
      });
    } catch (error) {
      console.error('[NetworkQuality] Failed to test:', error);
      // Note: Removed updateConnectionStatus call to avoid React errors
    } finally {
      setIsRunningQualityTest(false);
    }
  }, [isRunningQualityTest, testNetworkQuality]);

  // Handle online/offline events (PASSIVE - no state updates to avoid React errors)
  useEffect(() => {
    const handleOnline = () => {
      console.log('[OfflineStatus] Connection restored');
    };

    const handleOffline = () => {
      console.log('[OfflineStatus] Connection lost');
    };

    // Handle connection change events (mobile networks)
    const handleConnectionChange = () => {
      console.log('[OfflineStatus] Connection changed');
    };

    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    const connection = getNetworkConnection();
    if (connection) {
      connection.addEventListener?.('change', handleConnectionChange);
    }

    console.log('[OfflineStatus] Network monitoring initialized');

    // Cleanup
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      
      if (connection) {
        connection.removeEventListener?.('change', handleConnectionChange);
      }
    };
  }, []); // Empty dependencies to prevent re-runs

  // Periodic quality checks (every 30 seconds when online)
  useEffect(() => {
    if (!storeStatus.isOnline) return;
    
    const interval = setInterval(() => {
      runQualityTest();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [storeStatus.isOnline, runQualityTest]);

  // Track offline duration
  useEffect(() => {
    if (storeStatus.isOnline || !storeStatus.lastOnlineAt) return;
    
    const interval = setInterval(() => {
      const offlineDuration = Date.now() - storeStatus.lastOnlineAt!.getTime();
      // Use store directly to avoid circular dependency
      const { updateConnectionStatus } = useOfflineFormsStore.getState();
      updateConnectionStatus({ offlineDuration });
    }, 1000);

    return () => clearInterval(interval);
  }, [storeStatus.isOnline, storeStatus.lastOnlineAt]); // Remove updateStatus dependency

  return {
    // Core status
    isOnline: storeStatus.isOnline,
    isOffline: !storeStatus.isOnline,
    
    // Connection details
    quality: storeStatus.quality,
    networkType: storeStatus.networkType,
    lastOnlineAt: storeStatus.lastOnlineAt,
    offlineDuration: storeStatus.offlineDuration,
    
    // Quality testing
    isTestingQuality: isRunningQualityTest,
    testQuality: runQualityTest,
    
    // Helpers
    isGoodConnection: storeStatus.isOnline && storeStatus.quality === 'good',
    isPoorConnection: storeStatus.isOnline && storeStatus.quality === 'poor',
    shouldUseOfflineMode: !storeStatus.isOnline || storeStatus.quality === 'poor',
    
    // Connection info
    connectionInfo: getNetworkConnection()
  };
}